import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import DataScienceModelingImage from './DataScienceModelingImage'

const copy = {
  title: `Data science modeling & advanced analytics`,
  description: `Leverage out-of-the-box data science and analytics for personalized experiences that boost revenue and customer loyalty.`,
  elements: [
    {
      title: `Your data, decoded`,
      description: `Segment simplifies the process of converting an abundance of data into precise, actionable insights that elevate customer experiences and drive revenue growth, making advanced analytics and data science a snap.`,
    },
    {
      title: `Predictive insights for enhanced customer experiences`,
      description: `CustomerAI Predictions streamlines predicting future behavior and identifying key audiences primed for engagement, by leveraging predictive lifetime value (LTV), purchase likelihood, churn, or other tracked events, enabling personalized experiences without tapping technical teams.`,
    },
    {
      title: `Seamless integration with your data warehouse`,
      description: `Segment simplifies the training and activation of machine learning models by providing real-time, identity-resolved customer data that can be easily ported to your data warehouse.`,
    },
  ],
  buttonText: `Explore CustomerAI Predictions`,
  buttonUrl: `/solutions/ai/customerai-predictions/`,
}

const DataScienceModeling = () => {
  const theme = useTheme()

  return (
    <Wrapper id="data-science-modeling">
      <StyledSectionBaseTitle variant="noCopy">
        <Section.Contents>
          <CopyTextBase centered={true}>
            <CopyText.Heading tag="h2">{copy.title}</CopyText.Heading>
            <CopyText.Description>{copy.description}</CopyText.Description>
          </CopyTextBase>
        </Section.Contents>
      </StyledSectionBaseTitle>
      <StyledSectionBaseContent variant="2Col">
        <Section.Contents>
          <DataScienceModelingImage />
        </Section.Contents>
        <Section.Copy alignSelf="center">
          <ColumnTrack>
            {copy.elements.map(({ title, description }, key) => (
              <StyledFeatureBoxBase key={key} alignment="left" appearance="twilioLight" headerPosition="left">
                <FeatureBox.Body>
                  <FeatureBox.Title>{title}</FeatureBox.Title>
                  <FeatureBox.Text
                    tokens={{
                      color: theme.palette.twilio.gray[80],
                    }}
                    variant="pMedium"
                  >
                    {description}
                  </FeatureBox.Text>
                  {key === copy.elements.length - 1 && (
                    <CopyText.Link href={copy.buttonUrl}>{copy.buttonText}</CopyText.Link>
                  )}
                </FeatureBox.Body>
              </StyledFeatureBoxBase>
            ))}
          </ColumnTrack>
        </Section.Copy>
      </StyledSectionBaseContent>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const StyledFeatureBoxBase = styled(FeatureBoxBase)`
  gap: 0;
  grid-template-columns: 22px auto;
`

const StyledSectionBaseTitle = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 80px;
    padding-bottom: 0;
  }
`

const StyledSectionBaseContent = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 80px;
    padding-bottom: 80px;
  }
`

export default DataScienceModeling
